import React, { useCallback, useMemo } from 'react';
import { addDays, addMonths, format } from 'date-fns';
import { StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';
import Text from 'design-system/Text';
import Spacer from 'design-system/Spacer';
import { AppView } from 'design-system/AppView';
import IconArrow from 'design-system/icons/IconArrow';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { SCROLL_INDICATOR_INSETS } from 'design-system/values';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import { LimitStopOrderExpiryDateType } from '../types';
import { isWeb } from '../../../constants';
const LimitStopOrderSetExpiryDate = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const { onSelectExpiryDate } = route.params;
    const paddingBottom = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle);
    const onSelectOption = useCallback((type) => () => {
        switch (type) {
            case LimitStopOrderExpiryDateType.CUSTOM: {
                const date = new Date();
                navigation.navigateDeprecated('SelectDate', {
                    onSubmitDate: (dateString) => {
                        onSelectExpiryDate({
                            date: dateString,
                            type: LimitStopOrderExpiryDateType.CUSTOM,
                        });
                        navigation.pop(2);
                    },
                    props: {
                        minDate: format(date, `yyyy'-'MM'-'dd`),
                        maxDate: format(addMonths(date, 6), `yyyy'-'MM'-'dd`),
                    },
                    buttonText: 'Schedule',
                    title: 'Select custom expiry date',
                    longTitle: 'Select custom expiry date',
                    subTitle: 'Select any date in the next 6 months day of the month when the limit order should expire',
                });
                return;
            }
            case LimitStopOrderExpiryDateType.NINETY_DAYS:
                onSelectExpiryDate({ type: LimitStopOrderExpiryDateType.NINETY_DAYS });
                break;
            case LimitStopOrderExpiryDateType.TODAY:
                onSelectExpiryDate({ type: LimitStopOrderExpiryDateType.TODAY });
                break;
            case LimitStopOrderExpiryDateType.SIX_MONTHS:
                onSelectExpiryDate({ type: LimitStopOrderExpiryDateType.SIX_MONTHS });
                break;
            default:
                break;
        }
        navigation.goBack();
    }, [onSelectExpiryDate]);
    const data = useMemo(() => {
        const date = new Date();
        return [
            {
                title: '90 days',
                subTitle: format(addDays(date, 90), `d LLLL',' yyyy`),
                onPress: onSelectOption(LimitStopOrderExpiryDateType.NINETY_DAYS),
            },
            {
                title: 'Today',
                subTitle: format(date, `d LLLL',' yyyy`),
                onPress: onSelectOption(LimitStopOrderExpiryDateType.TODAY),
            },
            {
                title: '6 months',
                subTitle: format(addMonths(date, 6), `d LLLL',' yyyy`),
                onPress: onSelectOption(LimitStopOrderExpiryDateType.SIX_MONTHS),
            },
            {
                title: 'Custom',
                subTitle: 'Select any date in the next 6 months',
                onPress: onSelectOption(LimitStopOrderExpiryDateType.CUSTOM),
            },
        ];
    }, [onSelectOption]);
    return (<AppView withFrame={false}>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} contentContainerStyle={paddingBottom} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader smallSubTitle title="Set expiry date" subTitle="Choose when your limit order should expire"/>

          {data.map((eachData) => (<TouchableBounceCard onDark withPadding key={eachData.title} onPress={eachData.onPress} noMarginTop>
              <View style={styles.row}>
                <View style={styles.flex}>
                  <Text Text-16>{eachData.title}</Text>
                  <Spacer h={4}/>
                  <Text TextThin-14 Secondary>
                    {eachData.subTitle}
                  </Text>
                </View>
                <Spacer w={16}/>
                <IconArrow />
              </View>
            </TouchableBounceCard>))}
        </Animated.ScrollView>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
export default LimitStopOrderSetExpiryDate;
const styles = StyleSheet.create({
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    flex: {
        flex: 1,
    },
});
